import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Subject, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { UserService } from '../../../../@shared/services/user.service';
import { AuthUserService } from '../../../../@shared/services/auth/auth-user.service';
import { RafflesService } from './raffles.service';
import { getRaffleError } from './raffle-error.util';
import { IRaffle } from './raffle.interfaces';
import { RaffleRelationsDialogComponent } from './raffle-relations-dialog.component';
import { RaffleEditDialogComponent, IRaffleEditResult } from './raffle-edit-dialog.component';

@Component({
  selector: 'app-user-raffles',
  templateUrl: './raffles.component.html',
  styleUrls: ['./raffles.component.scss'],
})
export class RafflesComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();
  private activeDialog?: MatDialogRef<any>;
  userId: number;
  rows: IRaffle[] = [];
  amount = 0;
  pageIndex = 0;
  pageSize = 20;
  loading = false;
  mutating = false;
  error = '';
  canEdit = false;
  sort: Sort = { active: 'raffleTimeAt', direction: 'asc' };
  readonly fields = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Название' },
    {
      key: 'registrationStartAt',
      label: 'Старт регистрации',
      date: true,
      sort: true,
    },
    {
      key: 'registrationEndAt',
      label: 'Конец регистрации',
      date: true,
      sort: true,
    },
    { key: 'raffleTimeAt', label: 'Розыгрыш', date: true, sort: true },
    { key: 'accessType', label: 'Доступ' },
    { key: 'creatorId', label: 'Creator ID' },
    { key: 'raffleExp', label: 'Опыт' },
    { key: 'countParticipants', label: 'Участники' },
    { key: 'raffleFinish', label: 'Завершён' },
    { key: 'sumRewards', label: 'Сумма наград', sort: true },
    { key: 'sumConditions', label: 'Сумма условий', sort: true },
    { key: 'type', label: 'Тип' },
  ];
  readonly columns = [...this.fields.map(field => field.key), 'actions'];
  readonly dates = [
    { key: 'minRegistrationStartAt', label: 'Старт регистрации от' },
    { key: 'maxRegistrationStartAt', label: 'Старт регистрации до' },
    { key: 'minRegistrationEndAt', label: 'Конец регистрации от' },
    { key: 'maxRegistrationEndAt', label: 'Конец регистрации до' },
    { key: 'minRaffleTimeAt', label: 'Розыгрыш от' },
    { key: 'maxRaffleTimeAt', label: 'Розыгрыш до' },
  ];
  readonly form: FormGroup = this.fb.group({
    ids: '',
    creatorIds: '',
    accessTypes: [[] as string[]],
    raffleFinish: false,
    minSumRewards: null,
    maxSumRewards: null,
    minRegistrationStartAt: '',
    maxRegistrationStartAt: '',
    minRegistrationEndAt: '',
    maxRegistrationEndAt: '',
    minRaffleTimeAt: '',
    maxRaffleTimeAt: '',
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: RafflesService,
    private readonly userService: UserService,
    private readonly auth: AuthUserService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.auth.user$.pipe(takeUntil(this.destroyed$)).subscribe(user => {
      this.canEdit =
        user?.roles?.some((role: string) =>
          ['main_admin', 'main_owner'].includes(role),
        ) ?? false;
    });
    this.refresh$
      .pipe(
        switchMap(() => {
          this.loading = true;
          this.error = '';
          let params: object;
          try {
            params = this.params();
          } catch (error) {
            this.error =
              error instanceof Error ? error.message : 'Некорректные фильтры.';
            this.loading = false;
            return of(null);
          }
          return this.api.getRaffles(this.userId, params).pipe(
            catchError(error => {
              this.error = getRaffleError(error);
              return of(null);
            }),
            finalize(() => (this.loading = false)),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(page => {
        this.rows = page?.raffles ?? [];
        this.amount = page?.metadata.amount ?? 0;
        if (page && !this.rows.length && this.amount && this.pageIndex > 0) {
          this.pageIndex = Math.max(
            0,
            Math.ceil(this.amount / this.pageSize) - 1,
          );
          this.reload();
        }
      });
    this.userService.currentUserId
      .pipe(
        filter(id => Number(id) > 0),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(id => {
        this.activeDialog?.close();
        this.userId = Number(id);
        this.rows = [];
        this.pageIndex = 0;
        this.reload();
      });
  }

  reload(): void {
    if (this.userId) this.refresh$.next();
  }
  apply(): void {
    this.pageIndex = 0;
    this.reload();
  }
  reset(): void {
    this.form.reset({
      ids: '',
      creatorIds: '',
      accessTypes: [],
      raffleFinish: false,
    });
    this.apply();
  }
  page(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload();
  }
  sortChanged(sort: Sort): void {
    this.sort = sort;
    this.apply();
  }
  value(row: IRaffle, key: string): unknown {
    return row[key as keyof IRaffle];
  }
  dateValue(row: IRaffle, key: string): string | null {
    const value = this.value(row, key);
    return typeof value === 'string' ? value : null;
  }

  open(raffle: IRaffle, mode: 'conditions' | 'rewards'): void {
    this.activeDialog = this.dialog.open(RaffleRelationsDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      data: { userId: this.userId, raffle, mode, canEdit: this.canEdit },
    });
    this.activeDialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.reload());
  }

  exclude(raffle: IRaffle): void {
    const userId = this.userId;
    if (!this.canEdit || raffle.raffleFinish || this.mutating) return;
    this.activeDialog = this.dialog.open(RaffleEditDialogComponent, {
      width: '560px',
      data: {
        title: 'Исключить пользователя ' + userId + ' из раффла #' + raffle.id,
        description:
          'Будут удалены участие и все выполненные условия этого раффла. Билеты не возвращаются, награды не изменяются.',
      },
    });
    this.activeDialog
      .afterClosed()
      .pipe(
        filter((value: IRaffleEditResult | undefined): value is IRaffleEditResult => !!value),
        switchMap(value => {
          this.mutating = true;
          return this.api
            .exclude(userId, raffle.id, value.reason)
            .pipe(finalize(() => (this.mutating = false)));
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => this.reload(),
        error: error => (this.error = getRaffleError(error)),
      });
  }

  private params(): object {
    const value = this.form.value;
    const params: Record<string, unknown> = {
      pagination: {
        limit: this.pageSize,
        offset: this.pageIndex * this.pageSize,
      },
    };
    for (const field of ['ids', 'creatorIds']) {
      if (!value[field]?.trim()) continue;
      const ids = value[field]
        .split(/[\s,;]+/)
        .filter(Boolean)
        .map(Number);
      if (ids.some((id: number) => !Number.isSafeInteger(id) || id <= 0))
        throw new Error('ID должны быть положительными целыми числами.');
      params[field] = ids;
    }
    if (value.accessTypes?.length) params.accessTypes = value.accessTypes;
    if (value.raffleFinish != null) params.raffleFinish = value.raffleFinish;
    for (const field of ['minSumRewards', 'maxSumRewards']) {
      if (value[field] != null && value[field] !== '') {
        if (value[field] < 0)
          throw new Error('Сумма наград не может быть отрицательной.');
        params[field] = Number(value[field]);
      }
    }
    if (
      value.minSumRewards != null &&
      value.maxSumRewards != null &&
      value.minSumRewards > value.maxSumRewards
    ) {
      throw new Error('Минимальная сумма наград превышает максимальную.');
    }
    for (const date of this.dates)
      if (value[date.key])
        params[date.key] = new Date(value[date.key] + 'Z').toISOString();
    if (this.sort.direction) params.sort = this.sort;
    return params;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.activeDialog?.close();
  }
}
